import { ProposalSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { StationaryCheckoutChrome } from '@components/BookingFlow/Stationary/StationaryCheckoutChrome';
import { StationaryInvoiceNotice } from '@components/BookingFlow/Stationary/StationaryInvoiceNotice';
import { StationaryOrderSummaryCard } from '@components/BookingFlow/Stationary/StationaryOrderSummaryCard';
import { StationaryPaymentChoice } from '@components/BookingFlow/Stationary/StationaryPaymentChoice';
import { ProposalContactPhoneNotice } from '@components/BookingProposal/ProposalContactPhoneNotice';
import { ProposalCustomerAndLocation } from '@components/BookingProposal/ProposalCustomerAndLocation';
import { Trans } from '@lingui/react/macro';
import { invalidateQuery, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiLink, NoddiSkeleton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import type { ReactNode } from 'react';
import type { StationaryProposalCheckout } from '../../hooks/useStationaryProposalCheckout';

type Props = {
  /** Proposal slug for edit-time links. */
  slug: string;
  checkout: StationaryProposalCheckout;
  /** Content above checkout chrome (SMS login, org label, etc.). */
  beforeChrome?: ReactNode;
  onOrganizationNumberSaved?: () => void;
  /** Content after order summary (next steps, confirm CTAs, etc.). */
  afterSummary?: ReactNode;
};

/**
 * Shared stationary proposal checkout body: chrome + order summary.
 * Parents own address gate, hold expiry, and confirm CTAs.
 * Order: identity/address chrome → totals card (matches booking summary).
 */
export function StationaryProposalCheckoutBody({
  slug,
  checkout,
  beforeChrome,
  afterSummary,
  onOrganizationNumberSaved
}: Props) {
  const reservation = checkout.stationaryReservation;
  const timeWindowPath = customerRoutes.stationaryProposalTimeWindow.getPath({ slug });

  return (
    <div className='flex flex-col gap-4'>
      <ProposalSpikedTireSeasonBanner />
      {beforeChrome}

      {checkout.showsLoggedOutContactPhoneNotice && (
        <ProposalContactPhoneNotice phoneDisplay={checkout.displayedInvalidUserPhone} />
      )}

      <StationaryCheckoutChrome
        isLoggedIn={checkout.isLoggedIn}
        hasValidBookingPhone={checkout.hasValidContactPhone}
        canUpdateAccountPhone={checkout.canUpdateContactPhone}
        isLandline={checkout.isLandline}
        invalidPhoneDisplay={checkout.displayedInvalidUserPhone}
        organizationName={checkout.organizationName}
        registrationNumber={checkout.registrationNumber}
        showInvoiceBanner={false}
        requiresOrganizationNumber={checkout.requiresOrganizationNumber}
        organizationId={checkout.organizationId}
        onOrganizationNumberSaved={() => {
          onOrganizationNumberSaved?.();
          void invalidateQuery({ urlKey: URLKeys.getBookingProposalBySlug });
        }}
      />

      {checkout.isShoppingCartPending ? (
        <NoddiBasicCard className='flex flex-col gap-4 p-4 md:p-5'>
          <NoddiSkeleton className='h-5 w-1/2' />
          <NoddiSkeleton className='h-5 w-2/3' />
          <NoddiSkeleton className='h-40 w-full' />
        </NoddiBasicCard>
      ) : (
        reservation && (
          <StationaryOrderSummaryCard
            // Same header as the proposal landing card, so both steps open identically.
            header={
              <ProposalCustomerAndLocation
                customerName={checkout.customerName}
                customerPhoneNumber={checkout.customerPhoneNumber}
                locationName={checkout.locationName}
                locationAddress={checkout.locationAddress}
                sideBySide
              />
            }
            footerNotice={
              checkout.isInvoice && !checkout.showPaymentChoice ? (
                <StationaryInvoiceNotice
                  organizationName={checkout.invoiceTo?.name || checkout.organizationName}
                  registrationNumber={checkout.invoiceTo ? null : checkout.registrationNumber}
                />
              ) : undefined
            }
            customerName={checkout.customerName}
            customerPhoneNumber={checkout.customerPhoneNumber}
            customerIsDriver={checkout.customerIsDriver}
            locationName={checkout.locationName}
            locationAddress={checkout.locationAddress}
            timeWindow={{ startsAt: reservation.startsAt, endsAt: reservation.endsAt }}
            reservedUntil={reservation.reservedUntil}
            timeWindowAction={
              <NoddiLink to={timeWindowPath} size='extraSmall'>
                <Trans>Edit time</Trans>
              </NoddiLink>
            }
            carDisplayName={checkout.carDisplayName}
            lineItems={checkout.orderLineItems}
            discounts={checkout.orderDiscounts}
            totalPrice={checkout.orderTotalPrice}
          />
        )
      )}

      {checkout.showPaymentChoice && (
        <StationaryPaymentChoice
          paymentOption={checkout.paymentOption}
          onSelect={checkout.setPaymentOption}
          invoiceRecipientName={checkout.invoiceRecipientName}
        />
      )}

      {afterSummary}
    </div>
  );
}
